import { NOW_MIN, RELEASE_STEPS } from './fixtures';
import { finalLeft, isBlocked } from './logic';
import type { BlAmend, BlDraft, BlFinal, BlRelease, BlTab, LoiTemplate, Manifest, OriginalDoc } from './types';

// Số liệu mẫu của Mint-BL-iPad / -iPad-Detail / -Phone. Không có tiền: công nợ, phí AMD nằm sau quyền `money` (chưa mở) → chỉ hiện Có / Không / Vượt.

/** Board iPad chỉ có 6 Draft (không có d3, d6), 4 Final (không f3 đã gửi), 4 Release (không r2). */
export const PAD_IDS: Readonly<Record<'draft' | 'final' | 'rel' | 'amd' | 'mnf', readonly string[]>> = {
  draft: ['d1', 'd2', 'd4', 'd5', 'd7', 'd8'],
  final: ['f1', 'f2', 'f4', 'f5'],
  rel: ['r1', 'r3', 'r4', 'r5'],
  amd: ['a2', 'a1'],
  mnf: ['m1', 'm2'],
};

export const PAD_TABS: readonly { key: BlTab; label: string }[] = [
  { key: 'draft', label: 'Draft' }, { key: 'final', label: 'Final' }, { key: 'rel', label: 'Release' }, { key: 'amd', label: 'Sửa sau PH' },
  { key: 'loi', label: 'LOI' }, { key: 'orig', label: 'Chứng từ gốc' }, { key: 'mnf', label: 'Manifest' },
];

export type Tone = 'grey' | 'amber' | 'red' | 'green' | 'blue' | 'violet';
export interface PadRow {
  id: string;
  code: string;
  k: string;
  chip: string;
  tone: Tone;
  sub: string;
  right: string;
  /** màu chữ bên phải */
  rc: 'err' | 'warn' | '';
}

const pick = <T extends { id: string }>(rows: readonly T[], ids: readonly string[]): T[] => ids.flatMap((id) => rows.filter((r) => r.id === id));
const RESP: Record<BlDraft['resp'], [string, Tone]> = { none: ['Chưa gửi', 'grey'], wait: ['Chờ xác nhận', 'amber'], fix: ['Yêu cầu sửa', 'red'], ok: ['Đã xác nhận', 'green'] };

export function draftRows(all: readonly BlDraft[]): PadRow[] {
  return pick(all, PAD_IDS.draft).map((r) => {
    const [label, tone] = RESP[r.resp];
    const block = isBlocked(r);
    return { id: r.id, code: r.hbl, k: `${r.cus} → ${r.cne}`, chip: r.type, tone, sub: `${r.car} · ${r.ver ? `v${r.ver}` : 'chưa gửi'}`, right: block ? 'Thiếu LOI' : label, rc: block ? 'err' : '' };
  });
}

const leftText = (r: BlFinal): string => {
  const l = finalLeft(r, NOW_MIN);
  return l.kind === 'overdue' ? 'Quá hạn' : l.kind === 'left' ? `${l.hours}h ${String(l.minutes).padStart(2, '0')}p` : '';
};

export function finalRows(all: readonly BlFinal[]): PadRow[] {
  return pick(all, PAD_IDS.final).map((r) => {
    const l = finalLeft(r, NOW_MIN);
    const sent = r.st === 'sent';
    return { id: r.id, code: r.hbl, k: r.cus, chip: sent ? 'Đã gửi' : r.st === 'fail' ? 'Gửi lỗi' : 'Chưa gửi', tone: sent ? 'green' : r.st === 'fail' ? 'red' : 'amber', sub: `ATD ${r.atd.slice(12)}`, right: leftText(r), rc: l.kind === 'overdue' || (l.kind === 'left' && l.soon) ? 'err' : '' };
  });
}

export function releaseRows(all: readonly BlRelease[]): PadRow[] {
  return pick(all, PAD_IDS.rel).map((r) => ({
    id: r.id, code: r.job, k: r.cus, chip: r.type, tone: 'violet' as const,
    sub: r.step >= 5 ? 'Đã release' : ((RELEASE_STEPS as readonly string[])[r.step] ?? ''),
    right: r.money.debt ? (r.money.over ? 'Quá hạn' : 'Còn nợ') : 'Hết nợ', rc: r.money.over ? 'err' : '',
  }));
}

export function otherRows(tab: 'amd' | 'loi' | 'orig' | 'mnf', d: { amends: readonly BlAmend[]; lois: readonly LoiTemplate[]; originals: readonly OriginalDoc[]; manifests: readonly Manifest[] }): PadRow[] {
  if (tab === 'amd') return pick(d.amends, PAD_IDS.amd).map((r) => ({ id: r.id, code: r.hbl, k: `${r.cus} · ${r.what}`, chip: r.st === 'wait' ? 'Chờ Trưởng CS' : r.st === 'waived' ? 'Đã bỏ phí' : 'Có phí', tone: r.st === 'wait' ? 'amber' : r.st === 'waived' ? 'green' : 'grey', sub: '', right: '', rc: '' }));
  if (tab === 'loi') return d.lois.map((r) => ({ id: r.id, code: `${r.car} · ${r.kind}`, k: `${r.ver} · hiệu lực ${r.from}`, chip: r.st === 'ok' ? 'Đang dùng' : r.st === 'none' ? 'Dùng mẫu HANA chuẩn' : 'Mẫu cũ', tone: r.st === 'ok' ? 'green' : r.st === 'none' ? 'red' : 'amber', sub: '', right: `${r.uses} lần`, rc: '' }));
  if (tab === 'orig') return d.originals.map((r) => ({ id: r.id, code: `${r.kind} · ${r.no}`, k: r.cus, chip: r.st === 'hana' ? 'HANA · két' : r.st === 'cus' ? 'Đã ký nhận' : r.st === 'late' ? 'Quá hạn' : r.st === 'agent' ? 'Đại lý' : 'Chuyển phát', tone: r.st === 'hana' ? 'blue' : r.st === 'cus' ? 'green' : r.st === 'late' ? 'red' : 'amber', sub: '', right: '', rc: '' }));
  return pick(d.manifests, PAD_IDS.mnf).map((r) => ({ id: r.id, code: `${r.hbl} · ${r.dest}`, k: `${r.kind.split(' ')[0]} · ${r.st === 'done' ? (r.by ?? '') : `hạn ${r.due}`}`, chip: r.st === 'done' ? 'Đã khai' : 'Chưa ghi nhận', tone: r.st === 'done' ? 'green' : 'amber', sub: '', right: r.st === 'todo' ? 'chặn HBL' : '', rc: '' }));
}

// ---------- Điện thoại ----------
export type PhoneSeg = 'all' | 'draft' | 'final' | 'rel' | 'orig';
export const PHONE_SEGS: readonly { key: PhoneSeg; label: string }[] = [
  { key: 'all', label: 'Tất cả' }, { key: 'draft', label: 'Draft' }, { key: 'final', label: 'Final' }, { key: 'rel', label: 'Release' }, { key: 'orig', label: 'Chứng từ gốc' },
];

export interface PhoneBill {
  id: string;
  seg: Exclude<PhoneSeg, 'all'>;
  code: string;
  k: string;
  sub: string;
  chip: string;
  tone: Tone;
  right: string;
  rc: 'err' | 'warn' | '';
  job: string;
  type: string;
  car?: string;
  /** Draft / Final */
  cne?: string;
  pod?: string;
  vsl?: string;
  stamp?: string;
  /** Release (không có số tiền: chỉ cờ) */
  debt?: boolean;
  over?: boolean;
  overLimit?: boolean;
  step?: number;
  seat?: string;
  who?: [string, string];
  self?: boolean;
  mine?: boolean;
  terms?: string;
  missing?: string[];
  /** Chứng từ gốc */
  no?: string;
  kind?: string;
  signed?: boolean;
}

export const PHONE_SEL = 'r4';
export const PHONE_BILLS: readonly PhoneBill[] = [
  { id: 'd1', seg: 'draft', code: 'HNSGN260622360', k: 'YOSHIMI', type: 'Surrender', car: 'CNC', sub: 'CNC · Surrender · v2', chip: 'Yêu cầu sửa', tone: 'red', right: 'Thiếu LOI', rc: 'err', job: 'SE260622360', cne: 'YOSHIMI CO., LTD', pod: 'JPUKB', vsl: 'YM CERTAINTY 090N', stamp: 'DRAFT v2' },
  { id: 'd2', seg: 'draft', code: 'HNSGN260621750', k: 'ARRIVE', type: 'Surrender', car: 'CNC', sub: 'CNC · Surrender · v1', chip: 'Chờ xác nhận', tone: 'amber', right: 'hạn 15:00', rc: '', job: 'SE260621750', cne: 'UNIMAC CO.,LTD', pod: 'KRINC', vsl: 'SKY PRIDE 2608N', stamp: 'DRAFT v1' },
  { id: 'f1', seg: 'final', code: 'HNSGN260636900', k: 'SAVITA', type: 'Original', car: 'KMTC', sub: 'ATD 08:10 · hạn 10:10', chip: 'Final chưa gửi', tone: 'amber', right: 'còn 0h 50p', rc: 'warn', job: 'SE260636900', cne: 'Z-EUL CO., LTD', pod: 'KRINC', vsl: 'KMTC XIAMEN 2608N', stamp: 'FINAL' },
  { id: 'f5', seg: 'final', code: 'HNSGN260636080', k: 'GREENBUD', type: 'Original', car: 'SITC', sub: 'ATD 21/Oct 15:20', chip: 'Quá hạn', tone: 'red', right: 'quá 16h', rc: 'err', job: 'SE260636080', cne: 'THAI DUONG JOINT STOCK COMPANY', pod: 'JPNGO', vsl: 'SITC KANTO 2625N', stamp: 'FINAL' },
  { id: 'r4', seg: 'rel', code: 'SE260634770', k: 'ARRIVE', type: 'Surrender', car: 'Namsung', sub: 'Surrender · còn nợ', chip: 'Chờ BOD duyệt', tone: 'amber', right: 'có quá hạn', rc: 'err', job: 'SE260634770', debt: true, over: true, overLimit: true, step: 2, seat: '[BOD-DDIR] DAVID', who: ['MAI', 'DAVID'], self: true, mine: true, terms: 'TT 30 ngày sau ETD', missing: [] },
  { id: 'r1', seg: 'rel', code: 'SE260637580', k: 'GREENBUD', type: 'Surrender', car: 'Evergreen', sub: 'Surrender · còn nợ', chip: 'Chờ Trưởng KD', tone: 'amber', right: '', rc: '', job: 'SE260637580', debt: true, over: false, overLimit: false, step: 2, seat: '[SAL-MGR] DAVID', who: ['THUỶ', 'LINH'], mine: true, terms: 'TT 15 ngày sau ETD', missing: ['LOI bản ký đóng dấu'] },
  { id: 'r5', seg: 'rel', code: 'SE260622020', k: 'NEWWIND', type: 'Surrender', car: 'ONE', sub: 'Surrender · không còn nợ', chip: 'Sẵn sàng gửi', tone: 'blue', right: '', rc: '', job: 'SE260622020', debt: false, over: false, overLimit: false, step: 4, seat: '—', who: ['HÀ', 'LINH'], terms: 'Trả trước', missing: [] },
  { id: 'o1', seg: 'orig', code: 'OBL 3/3 · HNSGN260636090', k: 'GREENBUD', type: 'OBL 3/3', kind: 'OBL 3/3', no: 'HNSGN260636090', sub: 'Đang ở HANA · két', chip: 'Chờ giao tay', tone: 'violet', right: '', rc: '', job: 'SE260636090' },
  { id: 'o2', seg: 'orig', code: 'C/O form E · E2610 0081', k: 'GREENBUD', type: 'C/O form E', kind: 'C/O form E', no: 'E2610 0081 4412', sub: 'Ký 21/Oct 15:20 · GPS Q.1', chip: 'Đã ký nhận', tone: 'green', right: '', rc: '', job: 'SE260637580', signed: true },
];

export const SEATS = RELEASE_STEPS;

/** Luồng duyệt của một yêu cầu release: bước đã qua / hiện tại có người xử lý. */
export function phoneChain(b: PhoneBill): { k: string; sub: string; i: string; state: 'done' | 'cur' | 'todo' }[] {
  const step = b.step ?? 0;
  const who = [`[CS] ${b.who?.[0] ?? ''}`, `[Sale] ${b.who?.[1] ?? ''}`, `${b.seat ?? ''}${b.self ? ' · Tự duyệt' : ''}`, 'LOI bản ký · hãng xác nhận telex', 'CS gửi mail mẫu'];
  return SEATS.map((k, i) => ({ k: i === 2 && !b.debt ? `${k} · bỏ qua` : k, sub: i <= step ? who[i]! : '', i: i < step ? '✓' : String(i + 1), state: i < step ? 'done' : i === step ? 'cur' : 'todo' }));
}

/** Dòng "Trường — giá trị" của thẻ thông tin trên điện thoại. */
export function phoneFacts(b: PhoneBill): [string, string, boolean?][] {
  if (b.seg === 'rel') return [['Tổng nợ', b.debt ? 'Có' : 'Không'], ['Quá hạn', b.over ? 'Có' : 'Không', !!b.over], ['Hạn mức', b.overLimit ? 'Vượt' : 'Trong mức'], ['Điều khoản', b.terms ?? '']];
  if (b.seg === 'orig') return [['Loại', b.kind ?? ''], ['Đang ở', b.signed ? 'Khách' : 'HANA · két chứng từ'], ['Giao bằng', 'Tay · NGỌC ANH'], ['Ký nhận', b.signed ? 'Ảnh + chữ ký + giờ + GPS' : 'Chưa']];
  const draft = b.seg === 'draft';
  return [['Hãng · loại', `${b.car} · ${b.type}`], ['Trạng thái', b.chip, b.tone === 'red'], [draft ? 'LOI' : 'Hạn gửi', draft ? (b.id === 'd1' ? 'Thiếu · chặn gửi' : `Đã kèm mẫu ${b.car}`) : b.sub, b.id === 'd1']];
}

/** Trường in trên Draft B/L ở trang chi tiết iPad (board `DRAFT`). */
export interface PadDraftX { mbl: string; pol: string; pod: string; vsl: string; cont: string; gw: string }
export const PAD_DRAFT_X: Readonly<Record<string, PadDraftX>> = {
  d1: { mbl: 'ASC0545543', pol: 'Cát Lái', pod: 'JPUKB', vsl: 'YM CERTAINTY 090N', cont: '2 × 40HC · YMMU 6620418, YMMU 6620533', gw: '18.240 kg' },
  d2: { mbl: '—', pol: 'Cát Lái', pod: 'KRINC', vsl: 'SKY PRIDE 2608N', cont: '3 × 40HC', gw: '41.900 kg' },
  d4: { mbl: 'YMJAI490606867', pol: 'Cát Lái', pod: 'KRKAN', vsl: 'KMTC SHANGHAI 2611S', cont: 'LCL · 14,2 CBM', gw: '3.120 kg' },
  d5: { mbl: '—', pol: 'Cát Lái', pod: 'JPTYO', vsl: 'EVER WORLD 1763-015N', cont: '4 × 20DC', gw: '76.800 kg' },
  d7: { mbl: '341610081974', pol: 'Cát Lái', pod: 'KRPUS', vsl: 'YM CERTAINTY 089N', cont: '1 × 20DC', gw: '15.600 kg' },
  d8: { mbl: 'YMJAI490606868', pol: 'Cát Lái', pod: 'KRKAN', vsl: 'YM CERTAINTY 089N', cont: '1 × 40HC', gw: '12.400 kg' },
};
